import { takeLatest } from 'redux-saga/effects';
/* eslint-disable import/no-extraneous-dependencies */
import { actionChannel, call, put, take } from '@redux-saga/core/effects';
import B2ChatClient from '@src/client';
import {
  ContactStats,
  fetchUniqueContactsByRangeReport,
  fetchUniqueContactsByRangeReportFailure,
  fetchUniqueContactsByRangeReportFulfill,
  fetchUniqueContactsByRangeReportSuccess,
  fetchUniqueContactsFullReport,
  fetchUniqueContactsStatsReport,
  fetchUniqueContactsStatsReportFailure,
  fetchUniqueContactsStatsReportFulfill,
  fetchUniqueContactsStatsReportSuccess,
  fetchUniqueContactsSummaryReport,
  fetchUniqueContactsSummaryReportFailure,
  fetchUniqueContactsSummaryReportFulfill,
  fetchUniqueContactsSummaryReportSuccess,
  PriceByRange,
  RateSummary,
} from '@src/reducers/uniqueContactsReport';
import { B2ChatAPI } from '@src/types/api';
import { ErrorData } from '@types';
import { buffers, TakeableChannel } from 'redux-saga';

function* getUniqueContactsSummaryReportSaga(
  action: ReturnType<typeof fetchUniqueContactsSummaryReport>
) {
  try {
    const { error, data } = (yield call(
      B2ChatClient.resources.uniqueContactsReport.actions.summary,
      { params: action.payload }
    )) as B2ChatAPI.Response<RateSummary>;

    if (error) {
      yield put(fetchUniqueContactsSummaryReportFailure(error));
    } else {
      yield put(fetchUniqueContactsSummaryReportSuccess(data));
    }
  } catch (error) {
    yield put(fetchUniqueContactsSummaryReportFailure(error as ErrorData));
  } finally {
    yield put(fetchUniqueContactsSummaryReportFulfill());
  }
}

function* getUniqueContactsByRangeReportSaga(
  action: ReturnType<typeof fetchUniqueContactsByRangeReport>
) {
  try {
    const { error, data } = (yield call(
      B2ChatClient.resources.uniqueContactsReport.actions.byRange,
      {
        params: action.payload,
      }
    )) as B2ChatAPI.Response<PriceByRange>;

    if (error) {
      yield put(fetchUniqueContactsByRangeReportFailure(error));
    } else {
      yield put(fetchUniqueContactsByRangeReportSuccess(data));
    }
  } catch (error) {
    yield put(fetchUniqueContactsByRangeReportFailure(error as ErrorData));
  } finally {
    yield put(fetchUniqueContactsByRangeReportFulfill());
  }
}

function* getUniqueContactsStatsReportSaga(
  action: ReturnType<typeof fetchUniqueContactsStatsReport>
) {
  try {
    const { error, data } = (yield call(
      B2ChatClient.resources.uniqueContactsReport.actions.stats,
      { params: action.payload }
    )) as B2ChatAPI.Response<ContactStats>;

    if (error) {
      yield put(fetchUniqueContactsStatsReportFailure(error));
    } else {
      yield put(fetchUniqueContactsStatsReportSuccess(data));
    }
  } catch (error) {
    yield put(fetchUniqueContactsStatsReportFailure(error as ErrorData));
  } finally {
    yield put(fetchUniqueContactsStatsReportFulfill());
  }
}

function* getFullContactsReport(
  action: ReturnType<typeof fetchUniqueContactsFullReport>
) {
  const { startDate, endDate, granularityByRange, granularityByStats } =
    action.payload;

  yield call(getUniqueContactsSummaryReportSaga, {
    type: fetchUniqueContactsSummaryReport.type,
    payload: {
      startDate,
      endDate,
    },
  });
  yield call(getUniqueContactsByRangeReportSaga, {
    type: fetchUniqueContactsByRangeReport.type,
    payload: {
      startDate,
      endDate,
      granularity: granularityByRange,
    },
  });

  yield call(getUniqueContactsStatsReportSaga, {
    type: fetchUniqueContactsStatsReport.type,
    payload: {
      startDate,
      endDate,
      granularity: granularityByStats,
    },
  });
}

function* watchRequests() {
  const requestChannel: TakeableChannel<
    ReturnType<typeof fetchUniqueContactsFullReport>
  > = yield actionChannel(fetchUniqueContactsFullReport, buffers.sliding(1));
  while (true) {
    const action: ReturnType<typeof fetchUniqueContactsFullReport> = yield take(
      requestChannel
    );
    yield call(getFullContactsReport, action);
  }
}

export default function* uniqueContactsReportSaga() {
  yield takeLatest(
    fetchUniqueContactsSummaryReport,
    getUniqueContactsSummaryReportSaga
  );

  yield takeLatest(
    fetchUniqueContactsByRangeReport,
    getUniqueContactsByRangeReportSaga
  );

  yield takeLatest(
    fetchUniqueContactsStatsReport,
    getUniqueContactsStatsReportSaga
  );

  yield watchRequests();
}
